import { z } from 'zod';
import { DateValue } from '@internationalized/date';
import {
  refinePriceValidation,
  refinePriceRequiredValidation,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation/utils';
import {
  LegLocationTypeEnum,
  LegTypeEnum,
  RecommendedFlightType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
  GroundVehicleType,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { getPhoneSchema } from '@/utils/helpers';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';

// Schema chunks
export const BaseLocationSchema = z.object({
  id: z.string(),
  locationType: z.enum(LegLocationTypeEnum),
  locationTypeId: z.string(),
  displayName: z.string(),
  timezone: z.string(),
  longitude: z.number().nullable(),
  latitude: z.number().nullable(),
});

export const AirportLocationSchema = BaseLocationSchema.extend({
  locationType: z.literal(LegLocationTypeEnum.airport),
  maoId: z.string(),
  fboId: z.string(),
  fboAddressId: z.number(),
});

export const FacilityLocationSchema = BaseLocationSchema.extend({
  locationType: z.union([
    z.literal(LegLocationTypeEnum.hub),
    z.literal(LegLocationTypeEnum.homeResidence),
    z.literal(LegLocationTypeEnum.hospital),
    z.literal(LegLocationTypeEnum.txcenter),
  ]),
});

export const CustomAddressSchema = BaseLocationSchema.extend({
  locationType: z.literal(LegLocationTypeEnum.custom),
  hereMapsId: z.string(),
  country: z.string(),
  state: z.string(),
  city: z.string(),
  postalCode: z.string(),
  addressLine: z.string(),
});

export const LegLocationSchema = z.union([AirportLocationSchema, FacilityLocationSchema, CustomAddressSchema]);

export const BaseSchema = z.object({
  id: z.string(),
  formType: z.enum(LegTypeEnum),
  legType: z.enum(RouteLegTypeEnum),
  segmentType: z.enum(SegmentTypeEnum),
  groupId: z.string(),
  comments: z.string().max(MAX_LENGTH_2000, `Maximum comment's length: up to ${MAX_LENGTH_2000} symbols`),
  minDate: z.string().nullable(),
  maxDate: z.string().nullable(),
});

export const CommonRouteLegSchema = BaseSchema.extend({
  departureLocation: LegLocationSchema,
  arrivalLocation: LegLocationSchema,
  departureDateTimeValue: z.custom<DateValue>().nullable(),
  departureDateTime: z.string({ message: 'Provide departure time' }),
  departureLocalTimezone: z.string(),
  arrivalDateTimeValue: z.custom<DateValue>().nullable(),
  arrivalDateTime: z.string({ message: 'Provide arrival time' }),
  arrivalLocalTimezone: z.string(),
  isDateTimeLinked: z.boolean(),
  timeDurationInMs: z.number().min(0),
});

export const CommonLegResourcesSchema = CommonRouteLegSchema.extend({
  resources: z.array(z.object({ resourceId: z.string(), resourceType: z.enum(CaseResourceType) })).min(0),
  hasSCS: z.boolean(),
});

export const BaseAviaLegSchema = z.object({
  departureLocation: AirportLocationSchema,
  arrivalLocation: AirportLocationSchema,
  formType: z.literal(LegTypeEnum.air),
});

export const CommonAviaLegSchema = CommonLegResourcesSchema.extend(BaseAviaLegSchema.shape);

export const BaseGroundLegSchema = CommonLegResourcesSchema.extend({
  formType: z.literal(LegTypeEnum.ground),
  driver: z.string().min(0),
  email: z.email({ message: 'Please type correct email' }).or(z.literal('')),
  phoneNumber: getPhoneSchema({ isRequired: false }),
  plateNumber: z.string().min(0),
  vehicleType: z.enum(GroundVehicleType),
  reservationNumber: z.string().min(0),
  pickup: z.string().min(0),
  dropoff: z.string().min(0),
  wait: z.string().min(0),
  stay: z.string().min(0),
  distanceInMeters: z.number().min(0),
  isFeasibleForDrive: z.boolean(),
});

const crewMembersSchema = z
  .array(
    z.object({
      firstName: z.string(),
      lastName: z.string(),
      phoneNumber: z.string(),
    }),
  )
  .min(0);

export const BaseSupplementalLiftSchema = z.object({
  legType: z.literal(RouteLegTypeEnum.SupplementalLiftAviaLeg),
  supplementalLiftProviderId: z.string().min(1, 'Select Provider'),
  priceInCents: z.string().refine(refinePriceRequiredValidation, 'Provide total route Price'),
  tailNumber: z.string(),
  crewMembers: crewMembersSchema,
});

export const BaseTxProviderSchema = z.object({
  legType: z.literal(RouteLegTypeEnum.TxCenterProviderAviaLeg),
  txCenterProviderId: z.string().min(1, 'Select Provider'),
  tailNumber: z.string(),
  crewMembers: crewMembersSchema,
});

// Combined schemas
// AVIA FORMS
export const BaseTmdxAviaLegSchema = CommonAviaLegSchema.extend({
  legType: z.literal(RouteLegTypeEnum.TmdxJetAviaLeg),
  selectedAircraftId: z.string(),
});
export const BaseSupplementalLiftAviaSchema = CommonAviaLegSchema.extend(BaseSupplementalLiftSchema.shape);
export const BaseTxProviderAviaSchema = CommonAviaLegSchema.extend(BaseTxProviderSchema.shape);

export const CommonCommercialAviaLegSchema = CommonLegResourcesSchema.extend({
  departureLocation: AirportLocationSchema,
  arrivalLocation: AirportLocationSchema,
  formType: z.literal(LegTypeEnum.air),
  legType: z.literal(RouteLegTypeEnum.CommercialFlightAviaLeg),
  flightNumber: z.string().min(1, { message: 'Provide commercial flight number' }),
  priceInCents: z.string().refine(refinePriceValidation, 'Provide commercial flight price'),
  bookingNumber: z.string().min(0),
  flightOperator: z.string().optional(),
  numberOfSeats: z.number().optional(),
  seatType: z.string().optional(),
  commercialFlight: z.custom<RecommendedFlightType>().optional(),
  sabreGroupId: z.string().optional(),
});

export type CommonCommercialAviaLegSchemaValues = z.infer<typeof CommonCommercialAviaLegSchema>;
export type ConnectedLegSchemaType = Pick<
  CommonCommercialAviaLegSchemaValues,
  | 'departureLocation'
  | 'arrivalLocation'
  | 'departureDateTimeValue'
  | 'departureDateTime'
  | 'departureLocalTimezone'
  | 'arrivalDateTimeValue'
  | 'arrivalDateTime'
  | 'arrivalLocalTimezone'
  | 'flightNumber'
  | 'bookingNumber'
  | 'flightOperator'
  | 'numberOfSeats'
  | 'seatType'
>;

export const BaseCommercialAviaLegSchema = CommonCommercialAviaLegSchema.extend({
  connectedLegs: z.array(z.custom<ConnectedLegSchemaType>()).min(0),
});

// GROUND FORMS
export const BaseTmdxAffiliateGroundSchema = BaseGroundLegSchema.extend({
  legType: z.literal(RouteLegTypeEnum.TmdxAffiliateGroundLeg),
});
export const BaseTmdxDriveAffiliateGroundSchema = CommonLegResourcesSchema.extend({
  formType: z.literal(LegTypeEnum.ground),
  legType: z.literal(RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg),
  distanceInMeters: z.number().min(0),
  isFeasibleForDrive: z.boolean(),
  tripId: z.string().nullable(),
});
export const BaseTxCenterGroundSchema = BaseGroundLegSchema.extend({
  legType: z.literal(RouteLegTypeEnum.TxCenterProviderGroundLeg),
  txCenterProviderId: z.string().min(1, 'Select Provider'),
});
export const BaseCurriServiceGroundSchema = CommonLegResourcesSchema.extend({
  formType: z.literal(LegTypeEnum.ground),
  legType: z.literal(RouteLegTypeEnum.CurriServiceGroundLeg),
  trackingNumber: z.string().min(0),
});
export const BaseTmdxDriveTedsGroundSchema = CommonLegResourcesSchema.extend({
  formType: z.literal(LegTypeEnum.ground),
  legType: z.literal(RouteLegTypeEnum.TmdxDriveTedsGroundLeg),
  isFeasibleForDrive: z.boolean(),
  distanceInMeters: z.number().min(0),
  tripId: z.string().nullable(),
});
export const BaseSelfServicedGroundSchema = CommonLegResourcesSchema.extend({
  formType: z.literal(LegTypeEnum.ground),
  legType: z.literal(RouteLegTypeEnum.SelfServicedGroundLeg),
  distanceInMeters: z.number().min(0),
  isFeasibleForDrive: z.boolean(),
});

// ACCOMMODATION FORM
export const BaseAccommodationLegSchema = BaseSchema.extend({
  formType: z.literal(LegTypeEnum.accommodation),
  segmentType: z.literal(SegmentTypeEnum.custom),
  legType: z.literal(RouteLegTypeEnum.AccommodationLeg),
  checkInAtValue: z.custom<DateValue>().nullable(),
  checkInAt: z.string().min(1, 'Provide check-in date'),
  checkOutAtValue: z.custom<DateValue>().nullable(),
  checkOutAt: z.string().min(1, 'Provide check-out date'),
  hotelName: z.string().min(0),
  rooms: z.number().min(1),
  bookingNumber: z.string().min(0),
  arrivalLocation: CustomAddressSchema,
  hasScs: z.boolean(),
  resources: z
    .array(z.object({ resourceId: z.string(), resourceType: z.enum(CaseResourceType) }))
    .min(1, 'At least one resource should be selected'),
});
